/**
 * Team Gamification Mycelium Connector
 *
 * Extends individual gamification to cross-org leaderboards, team challenges,
 * and achievement cascades. Listens for individual XP awards and aggregates
 * them into team-level metrics; publishes team events so other connectors
 * (notification escalation, cortex context, sales pipeline) can react.
 *
 * Phase 4 connector — builds on top of the Phase 1 Activity-Gamification
 * connector without modifying it.
 *
 * Mycelium Principles:
 * - Economy of Movement: Re-uses individual XP events — no extra logging
 * - Gravitational Organisation: Teams form natural clusters
 * - Holographic: Each team score mirrors the state of its members
 * - Self-Organizing: Teams that produce events get ranked automatically
 *
 * @module mycelium-team-gamification
 * @since 2026-03-02
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

/** Minimum team members required for team ranking */
const MIN_TEAM_SIZE_FOR_RANKING = 2;

/** How long a challenge period lasts (7 days) */
const CHALLENGE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;

/** XP thresholds for team milestones */
const TEAM_MILESTONES = [500, 1000, 2500, 5000, 10000, 25000, 50000] as const;

/** Team streak multipliers (consecutive active days) */
const TEAM_STREAK_MULTIPLIERS: Record<number, number> = {
  3: 1.1,
  7: 1.25,
  14: 1.5,
  30: 2.0,
};

// ============================================================================
// TYPES
// ============================================================================

interface TeamScore {
  teamId: string;
  orgId: string;
  totalXp: number;
  memberXp: Map<string, number>;
  activeDays: Set<string>;
  currentStreak: number;
  lastActivityDate: string;
  milestonesReached: number[];
  challengeXp: number;
  challengeStartedAt: number;
}

interface TeamChallenge {
  id: string;
  name: string;
  description: string;
  targetXp: number;
  startedAt: number;
  endsAt: number;
  teamScores: Map<string, number>;
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class TeamGamificationConnector {
  private initialized = false;
  private teamScores = new Map<string, TeamScore>();
  private activeChallenges = new Map<string, TeamChallenge>();
  private stats = {
    teamXpAwarded: 0,
    teamMilestonesReached: 0,
    challengesCreated: 0,
    leaderboardUpdates: 0,
  };

  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("TeamGamificationConnector already initialized");
      return;
    }

    logger.info("🏆 Team Gamification connector initializing");

    // Listen for individual XP awards from gamification domain
    myceliumEventBackbone.subscribe({
      name: "TeamGamificationConnector",
      domains: ["gamification"],
      eventTypes: [
        "xp_awarded",
        "milestone_achieved",
        "streak_updated",
        "achievement_unlocked",
      ],
      handler: this.handleEvent.bind(this),
      priority: 6, // After individual gamification (7), before audit trail (1)
    });

    // Also listen for team membership events
    myceliumEventBackbone.subscribe({
      name: "TeamGamificationConnector:team",
      domains: ["team"],
      eventTypes: [
        "team_created",
        "team_member_added",
        "team_member_removed",
      ],
      handler: this.handleTeamEvent.bind(this),
      priority: 6,
    });

    this.initialized = true;
    logger.info("✅ Team Gamification connector ready — aggregating team XP");
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    this.teamScores.clear();
    this.activeChallenges.clear();
    logger.info("🏆 Team Gamification connector disposed", { stats: this.stats });
  }

  /**
   * Get leaderboard for an org — sorted by total team XP
   */
  getOrgLeaderboard(orgId: string): Array<{
    teamId: string;
    totalXp: number;
    memberCount: number;
    currentStreak: number;
    avgXpPerMember: number;
  }> {
    const teams = Array.from(this.teamScores.values())
      .filter(t => t.orgId === orgId && t.memberXp.size >= MIN_TEAM_SIZE_FOR_RANKING)
      .sort((a, b) => b.totalXp - a.totalXp)
      .map(t => ({
        teamId: t.teamId,
        totalXp: t.totalXp,
        memberCount: t.memberXp.size,
        currentStreak: t.currentStreak,
        avgXpPerMember: t.memberXp.size > 0 ? Math.round(t.totalXp / t.memberXp.size) : 0,
      }));

    return teams;
  }

  /**
   * Create a team challenge (e.g. "Earn 5000 XP in 7 days")
   */
  async createChallenge(
    name: string,
    description: string,
    targetXp: number,
    durationMs: number = CHALLENGE_PERIOD_MS
  ): Promise<string> {
    const id = `challenge_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const now = Date.now();

    this.activeChallenges.set(id, {
      id,
      name,
      description,
      targetXp,
      startedAt: now,
      endsAt: now + durationMs,
      teamScores: new Map(),
    });

    this.stats.challengesCreated++;

    await myceliumEventBackbone.publish({
      domain: "gamification",
      type: "team_challenge_created",
      payload: {
        challengeId: id,
        name,
        description,
        targetXp,
        durationMs,
        source: "TeamGamificationConnector",
      },
    });

    logger.info("🏆 Team challenge created", { id, name, targetXp });
    return id;
  }

  getStats(): typeof this.stats {
    return { ...this.stats };
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private async handleEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, teamId, orgId, payload } = event;

    try {
      // Need at least teamId to aggregate
      if (!teamId || !userId) return;

      switch (type) {
        case "xp_awarded":
          await this.handleXpAwarded(userId, teamId, orgId || "default", payload);
          break;
        case "milestone_achieved":
          await this.handleMilestoneAchieved(userId, teamId, orgId || "default", payload);
          break;
        case "streak_updated":
          this.handleStreakUpdated(userId, teamId, orgId || "default", payload);
          break;
        case "achievement_unlocked":
          await this.handleAchievementUnlocked(userId, teamId, orgId || "default", payload);
          break;
      }
    } catch (error) {
      logger.error("❌ TeamGamification handler error", {
        eventType: type,
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private async handleTeamEvent(event: MyceliumEvent): Promise<void> {
    const { type, payload } = event;

    try {
      const teamId = (payload.teamId as string) || event.teamId;
      const orgId = (payload.orgId as string) || event.orgId || "default";

      if (!teamId) return;

      switch (type) {
        case "team_created":
          this.ensureTeamScore(teamId, orgId);
          break;
        case "team_member_added": {
          const team = this.ensureTeamScore(teamId, orgId);
          const memberId = payload.userId as string;
          if (memberId && !team.memberXp.has(memberId)) {
            team.memberXp.set(memberId, 0);
          }
          break;
        }
        case "team_member_removed": {
          const team = this.teamScores.get(teamId);
          const removedId = payload.userId as string;
          if (team && removedId) {
            team.memberXp.delete(removedId);
          }
          break;
        }
      }
    } catch (error) {
      logger.error("❌ TeamGamification team event handler error", {
        eventType: type,
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  private async handleXpAwarded(
    userId: string,
    teamId: string,
    orgId: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    const xp = (payload.xp as number) || 0;
    if (xp <= 0) return;

    const team = this.ensureTeamScore(teamId, orgId);

    // Apply team streak multiplier
    const multiplier = this.getTeamStreakMultiplier(team.currentStreak);
    const teamXp = Math.round(xp * multiplier);

    // Update member XP
    const currentMemberXp = team.memberXp.get(userId) || 0;
    team.memberXp.set(userId, currentMemberXp + teamXp);

    // Update team total
    team.totalXp += teamXp;

    // Track active days
    const today = new Date().toISOString().split("T")[0];
    team.activeDays.add(today);
    if (team.lastActivityDate !== today) {
      this.updateTeamStreak(team, today);
    }

    this.stats.teamXpAwarded += teamXp;

    // Check milestones
    await this.checkTeamMilestones(team);

    // Update challenge progress
    await this.updateChallengeProgress(teamId, teamXp);

    // Publish team XP event
    await myceliumEventBackbone.publish({
      domain: "gamification",
      type: "team_xp_awarded",
      userId,
      teamId,
      orgId,
      payload: {
        individualXp: xp,
        teamXp,
        multiplier,
        teamTotal: team.totalXp,
        memberCount: team.memberXp.size,
        source: "TeamGamificationConnector",
      },
    });

    this.stats.leaderboardUpdates++;
  }

  private async handleMilestoneAchieved(
    userId: string,
    teamId: string,
    orgId: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    // When an individual achieves a milestone, give team a bonus
    const bonusXp = 50;
    const team = this.ensureTeamScore(teamId, orgId);
    team.totalXp += bonusXp;

    await myceliumEventBackbone.publish({
      domain: "gamification",
      type: "team_milestone_bonus",
      userId,
      teamId,
      orgId,
      payload: {
        bonusXp,
        reason: "member_milestone",
        memberMilestone: payload.milestone,
        teamTotal: team.totalXp,
        source: "TeamGamificationConnector",
      },
    });
  }

  private handleStreakUpdated(
    userId: string,
    teamId: string,
    orgId: string,
    payload: Record<string, unknown>
  ): void {
    // Track individual streaks to calculate team consistency
    const team = this.ensureTeamScore(teamId, orgId);
    const today = new Date().toISOString().split("T")[0];
    team.activeDays.add(today);
  }

  private async handleAchievementUnlocked(
    userId: string,
    teamId: string,
    orgId: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    // Achievement cascade: unlock team-level achievement when enough members earn it
    await myceliumEventBackbone.publish({
      domain: "gamification",
      type: "team_achievement_cascade",
      userId,
      teamId,
      orgId,
      payload: {
        individualAchievement: payload.achievement,
        teamId,
        source: "TeamGamificationConnector",
      },
    });
  }

  private ensureTeamScore(teamId: string, orgId: string): TeamScore {
    let team = this.teamScores.get(teamId);
    if (!team) {
      team = {
        teamId,
        orgId,
        totalXp: 0,
        memberXp: new Map(),
        activeDays: new Set(),
        currentStreak: 0,
        lastActivityDate: "",
        milestonesReached: [],
        challengeXp: 0,
        challengeStartedAt: Date.now(),
      };
      this.teamScores.set(teamId, team);
    }
    return team;
  }

  private updateTeamStreak(team: TeamScore, today: string): void {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    if (team.lastActivityDate === yesterdayStr) {
      team.currentStreak++;
    } else if (team.lastActivityDate !== today) {
      team.currentStreak = 1;
    }

    team.lastActivityDate = today;
  }

  private getTeamStreakMultiplier(streak: number): number {
    let multiplier = 1.0;
    for (const [days, mult] of Object.entries(TEAM_STREAK_MULTIPLIERS)) {
      if (streak >= Number(days)) {
        multiplier = mult;
      }
    }
    return multiplier;
  }

  private async checkTeamMilestones(team: TeamScore): Promise<void> {
    for (const milestone of TEAM_MILESTONES) {
      if (team.totalXp >= milestone && !team.milestonesReached.includes(milestone)) {
        team.milestonesReached.push(milestone);
        this.stats.teamMilestonesReached++;

        await myceliumEventBackbone.publish({
          domain: "gamification",
          type: "team_milestone_reached",
          teamId: team.teamId,
          orgId: team.orgId,
          payload: {
            milestone,
            teamTotal: team.totalXp,
            memberCount: team.memberXp.size,
            source: "TeamGamificationConnector",
          },
        });

        logger.info("🏆 Team milestone reached", {
          teamId: team.teamId,
          milestone,
          totalXp: team.totalXp,
        });
      }
    }
  }

  private async updateChallengeProgress(teamId: string, xp: number): Promise<void> {
    const now = Date.now();

    for (const [challengeId, challenge] of this.activeChallenges) {
      // Skip expired challenges
      if (now > challenge.endsAt) {
        this.activeChallenges.delete(challengeId);
        continue;
      }

      const currentScore = challenge.teamScores.get(teamId) || 0;
      const newScore = currentScore + xp;
      challenge.teamScores.set(teamId, newScore);

      // Check if team completed the challenge
      if (newScore >= challenge.targetXp && currentScore < challenge.targetXp) {
        await myceliumEventBackbone.publish({
          domain: "gamification",
          type: "team_challenge_completed",
          teamId,
          payload: {
            challengeId,
            challengeName: challenge.name,
            teamScore: newScore,
            targetXp: challenge.targetXp,
            source: "TeamGamificationConnector",
          },
        });

        logger.info("🏆 Team completed challenge!", {
          teamId,
          challengeId,
          challengeName: challenge.name,
        });
      }
    }
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const teamGamificationConnector = new TeamGamificationConnector();
